import {
  AwardCriterionAnswerChoice,
  AwardSubCriterionAnswerFormat,
  type EvalCriterionDto,
  type EvalSupplierAnswerDto,
} from "../../../../autogen/bff-api";
import { type ReadonlyAnswer } from "../../../sourcing-events/bid-readonly/ReadonlyAnswerCard";

export const presentSupplierAnswer = (
  answer: EvalSupplierAnswerDto | null | undefined,
): EvalSupplierAnswerDto | undefined => {
  if (answer == null) return undefined;
  const hasContent =
    (answer.text ?? "").trim().length > 0 ||
    answer.number != null ||
    answer.choice != null ||
    (answer.attachments?.length ?? 0) > 0;
  return hasContent ? answer : undefined;
};

const EMPTY: ReadonlyAnswer = { kind: "empty" };
const ANSWERED_BY_ATTACHMENT: ReadonlyAnswer = { kind: "text", value: "" };

const inFormat = (
  answer: EvalSupplierAnswerDto,
  format: AwardSubCriterionAnswerFormat,
): ReadonlyAnswer => {
  switch (format) {
    case AwardSubCriterionAnswerFormat.FreeText:
      return (answer.text ?? "").trim().length > 0 ? { kind: "text", value: answer.text ?? "" } : EMPTY;
    case AwardSubCriterionAnswerFormat.Number:
      return answer.number != null ? { kind: "number", value: answer.number } : EMPTY;
    case AwardSubCriterionAnswerFormat.YesNoPartial:
      return answer.choice != null
        ? {
            kind: "choice",
            value: answer.choice as AwardCriterionAnswerChoice,
            elaborationHtml: answer.partialElaboration ?? null,
          }
        : EMPTY;
    case AwardSubCriterionAnswerFormat.Document:
      return EMPTY;
  }
};

const CARRYING_FORMATS = [
  AwardSubCriterionAnswerFormat.FreeText,
  AwardSubCriterionAnswerFormat.Number,
  AwardSubCriterionAnswerFormat.YesNoPartial,
] as const;

export const toReadonlyAnswer = (
  answer: EvalSupplierAnswerDto | null | undefined,
  criterion: EvalCriterionDto,
): ReadonlyAnswer => {
  if (answer == null) return EMPTY;
  const answered = inFormat(answer, criterion.answerFormat ?? AwardSubCriterionAnswerFormat.FreeText);
  if (answered.kind !== "empty") return answered;
  // Content in the "wrong" field still outranks the placeholder: BidV2Service scores an incomplete award
  // answer as a PreflightWarning, and a private event's change-in-flight can amend answerFormat after bids.
  for (const other of CARRYING_FORMATS) {
    const carried = inFormat(answer, other);
    if (carried.kind !== "empty") return carried;
  }
  return (answer.attachments ?? []).length > 0 ? ANSWERED_BY_ATTACHMENT : EMPTY;
};
